import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useContext,
} from "react";

import { isSameDay, parseISO, format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import openSocket from "../../services/socket-io";
import {
  Clock,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Ban,
  Download,
  Loader2,
  Star,
  SlidersHorizontal,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Eye,
  FileArchive,
  FileSpreadsheet,
  FileText,
  BadgeCheck,
  UserPlus,
  Pencil,
  Forward,
  LockKeyhole,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { useLocalStorage } from "../../hooks/useLocalStorage";

import MarkdownWrapper from "../MarkdownWrapper";
import TemplateMessage from "../TemplateMessage";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import { lerLocalizacao } from "../../utils/localizacao";
import { lerVcards } from "../../utils/vcard";
import ModalImageCors from "../ModalImageCors";
import VideoNaConversa from "../VideoNaConversa";
import HistoricoAtendimentos from "../HistoricoAtendimentos";
import MessageOptionsMenu from "../MessageOptionsMenu";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";
import MiniaturaDePdf from "../MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import TranscricaoDeAudio from "../TranscricaoDeAudio";

// O socket manda a mensagem inteira a cada atualização (ack, edição), e nem
// todo caminho enriquece as menções. Substituir sem mais nada apagava os
// `mentionNames` já recebidos e a menção voltava a aparecer como número.
const preservarMencoes = (nova, antiga) =>
  nova?.mentionNames?.length || !antiga?.mentionNames?.length
    ? nova
    : { ...nova, mentionNames: antiga.mentionNames };

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = preservarMencoes(newMessage, state[messageIndex]);
      return [...state];
    }

    // Entra pela data, não no fim da lista. O socket não garante ordem de
    // chegada (mídia baixada demora mais que texto, e o eco do WhatsApp pode
    // vir depois do que já foi gravado), e empurrar sempre para o fim deixava
    // a conversa fora de ordem até dar F5.
    const nova = [...state, newMessage];
    nova.sort((a, b) => {
      const diff = new Date(a.createdAt) - new Date(b.createdAt);
      return diff !== 0 ? diff : String(a.id).localeCompare(String(b.id));
    });

    return nova;
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = preservarMencoes(
        messageToUpdate,
        state[messageIndex]
      );
    }

    return [...state];
  }

  // Desfavoritar com o filtro "só favoritas" ligado tira a mensagem da lista
  // sem precisar recarregar a conversa inteira.
  if (action.type === "REMOVE_MESSAGE") {
    return state.filter((m) => m.id !== action.payload);
  }

  // As reações moram na própria mensagem, e não num mapa à parte, porque é
  // assim que elas são desenhadas: um chip embaixo daquele balão. O payload
  // aceita as duas formas em que elas chegam — o mapa inteiro que vem junto
  // com a página de mensagens (`mapa`) e a atualização de uma só, que vem pelo
  // socket ou da resposta do POST.
  if (action.type === "SET_REACTION") {
    const mapa = action.payload.mapa || {
      [action.payload.messageId]: action.payload.reacoes,
    };

    return state.map((m) =>
      mapa[m.id] !== undefined ? { ...m, reacoes: mapa[m.id] } : m,
    );
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Quantas páginas a mais o sistema aceita carregar sozinho atrás de um
// resultado de busca antigo. Sem teto, escolher a primeira mensagem de uma
// conversa de dois anos puxaria a conversa inteira, de 20 em 20.
// Quantas páginas de 20 a busca puxa para trás até alcançar a mensagem. Subiu
// de 25 para 60 quando a conversa passou a mostrar o histórico do contato: o
// que se procura costuma estar num atendimento anterior, muito mais atrás.
const MAX_PAGINAS_ATRAS_DO_ALVO = 60;

const MessagesList = ({ ticketId, isGroup }) => {
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  // Se a conversa deve continuar colada no fim enquanto o conteúdo cresce.
  // Verdadeiro ao abrir e enquanto quem lê estiver no fim; falso assim que a
  // pessoa rola para cima, senão ler o histórico seria impossível — cada
  // imagem que terminasse de carregar jogaria a conversa de volta para baixo.
  const grudarNoFim = useRef(true);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  // Filtros da conversa. Ficam aqui, e não no pai, porque quem pagina e
  // acumula as mensagens é este componente — mudar o filtro tem que zerar a
  // lista e voltar para a página 1, senão as mensagens do filtro antigo ficam
  // misturadas com as do novo.
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [apenasFavoritas, setApenasFavoritas] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Busca de texto dentro da conversa. Fica separada dos filtros acima porque
  // ela NÃO recorta a lista: a conversa continua inteira e a busca só aponta
  // para onde ir — como no WhatsApp.
  const [termoBusca, setTermoBusca] = useState("");
  const [achados, setAchados] = useState([]);
  const [indiceAchado, setIndiceAchado] = useState(0);
  const [buscando, setBuscando] = useState(false);
  // Id da mensagem que a busca quer alcançar e id da que está piscando.
  const [alvoBusca, setAlvoBusca] = useState(null);
  const [destacada, setDestacada] = useState(null);
  const paginasPuxadasRef = useRef(0);

  // Ids favoritados POR ESTE atendente. Vem do backend a cada página; o Set
  // acumula porque a conversa é carregada aos poucos.
  const [favoritos, setFavoritos] = useState(() => new Set());

  // Escolher arquivos na própria conversa. Ligado o modo, a bolha deixa de
  // abrir o arquivo e passa a marcar/desmarcar — só as que TÊM anexo; clicar
  // numa mensagem de texto não faz nada, para não dar a impressão de que ela
  // vai no ZIP.
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  // Participantes do grupo que acabaram de entrar na agenda: a lista de
  // mensagens vem do reducer e não recarrega só por causa disto.
  const [adicionados, setAdicionados] = useState(() => new Set());
  // De que atendimento é cada mensagem: número, fila e situação. É o que
  // permite desenhar a faixa entre um chamado e o seguinte.
  const [atendimentos, setAtendimentos] = useState({});
  // Hora com segundos na bolha. Fica na escolha de cada um e é lembrada: no
  // uso normal "14:32" basta, mas quem está conferindo a ordem de uma rajada
  // precisa do segundo.
  const [horaDetalhada, setHoraDetalhada] = useLocalStorage(
    "horaComSegundos",
    false,
  );
  const [adicionando, setAdicionando] = useState(null);
  const [baixando, setBaixando] = useState(null);

  // Arquivo aberto no visualizador (índice dentro de `arquivosDaConversa`).
  const [visualizando, setVisualizando] = useState(null);

  // Citar a mensagem no campo de resposta — o mesmo estado que o menu da bolha
  // usa; aqui ele serve ao atalho de dois cliques.
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  const filtrando = Boolean(dataInicio || dataFim || apenasFavoritas);

  // Ref, e não dependência do efeito do socket: colocar `filtrando` nas deps
  // faria o Socket.IO desconectar e reconectar a cada tecla no campo de data.
  const filtrandoRef = useRef(false);
  filtrandoRef.current = filtrando;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setFavoritos(new Set());
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
    limparBusca();

    currentTicketId.current = ticketId;
  }, [ticketId]);

  // Trocar o filtro descarta o que já estava carregado: a lista acumulada é de
  // outro recorte.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [dataInicio, dataFim, apenasFavoritas]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: {
              pageNumber,
              dataInicio: dataInicio || undefined,
              dataFim: dataFim || undefined,
              apenasFavoritas: apenasFavoritas ? "true" : undefined,
            },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            // Depois do LOAD: as reações só têm onde pousar quando as
            // mensagens da página já estão na lista.
            if (data.reactions) {
              dispatch({
                type: "SET_REACTION",
                payload: { mapa: data.reactions },
              });
            }
            setHasMore(data.hasMore);
            setLoading(false);

            if (data.atendimentos) {
              setAtendimentos((anterior) => ({
                ...anterior,
                ...data.atendimentos,
              }));
            }

            if (data.favoritedIds?.length) {
              setFavoritos((anterior) => {
                const novo = new Set(anterior);
                data.favoritedIds.forEach((id) => novo.add(id));
                return novo;
              });
            }
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, dataInicio, dataFim, apenasFavoritas]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      // Só o que é DESTE atendimento.
      //
      // A conexão do Socket.IO é uma só para o app inteiro, e a lista de
      // tickets e as notificações entram nas salas "open"/"pending"/
      // "notification" — para onde o backend também manda o appMessage de
      // cada mensagem nova. Sem esta conferência, a conversa aberta recebia
      // mensagem de TODOS os atendimentos da empresa e as misturava no fim
      // da lista.
      if (Number(data.message?.ticketId) !== Number(ticketId)) return;

      if (data.action === "create") {
        // Com filtro ligado a lista é um recorte (um período, ou só as
        // favoritas). Empurrar a mensagem nova ali dentro furaria o recorte:
        // ela apareceria fora do período escolhido, sem estar favoritada.
        if (filtrandoRef.current) return;

        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    // A reação é emitida na sala da empresa, então chega a reação de qualquer
    // atendimento — a mesma peneira do appMessage vale aqui.
    socket.on("appMessageReaction", (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  // Busca dentro da conversa. Debounce para não disparar uma requisição por
  // tecla, e mínimo de 2 caracteres porque o backend recusa menos que isso.
  useEffect(() => {
    const termo = termoBusca.trim();

    if (termo.length < 2) {
      setAchados([]);
      setIndiceAchado(0);
      setBuscando(false);
      return undefined;
    }

    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/search`, {
          params: { q: termo },
        });

        if (currentTicketId.current !== ticketId) return;

        const encontrados = data.achados || [];

        setAchados(encontrados);
        setIndiceAchado(0);

        // Vai direto para o primeiro. Sem isto, com um resultado só não havia
        // como alcançá-lo: as setas só aparecem quando há mais de um.
        if (encontrados.length) {
          paginasPuxadasRef.current = 0;
          setAlvoBusca(encontrados[0].id);
        }
      } catch (err) {
        setAchados([]);
        toastError(err);
      }
      setBuscando(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [termoBusca, ticketId]);

  // Levar a conversa até a mensagem escolhida.
  //
  // Limitação assumida: a mensagem pode estar numa página que ainda não foi
  // carregada. Quando ela não está no DOM, o efeito pede mais uma página e se
  // repete quando a lista muda — ou seja, ele "rola para trás" sozinho até
  // achar. Isso tem custo (uma requisição por página, de 20 em 20), por isso o
  // teto do MAX_PAGINAS_ATRAS_DO_ALVO: passando disso a busca desiste e avisa,
  // em vez de puxar a conversa inteira em silêncio.
  useEffect(() => {
    if (!alvoBusca) return undefined;

    const elemento = document.getElementById(`mensagem-${alvoBusca}`);

    if (elemento) {
      elemento.scrollIntoView({ block: "center", behavior: "smooth" });
      setDestacada(alvoBusca);
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;

      const timer = setTimeout(() => setDestacada(null), 3000);
      return () => clearTimeout(timer);
    }

    if (loading) return undefined;

    if (!hasMore || paginasPuxadasRef.current >= MAX_PAGINAS_ATRAS_DO_ALVO) {
      toast.info(
        "Essa mensagem é antiga demais para ser aberta pela busca. Role a conversa para cima para carregá-la.",
      );
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;
      return undefined;
    }

    paginasPuxadasRef.current += 1;
    loadMore();

    return undefined;
  }, [alvoBusca, messagesList, hasMore, loading]);

  const irParaAchado = (indice) => {
    const achado = achados[indice];
    if (!achado) return;

    setIndiceAchado(indice);
    paginasPuxadasRef.current = 0;
    setAlvoBusca(achado.id);
  };

  const limparBusca = () => {
    setTermoBusca("");
    setAchados([]);
    setIndiceAchado(0);
    setAlvoBusca(null);
    setDestacada(null);
  };

  const reagir = async (message, emoji) => {
    try {
      const { data } = await api.post(`/messages/${message.id}/reaction`, {
        emoji,
      });

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  // Segura a conversa no fim enquanto o conteúdo ainda está crescendo.
  //
  // É o que conserta de verdade o "abre no meio": a rolagem inicial acontece
  // com imagem, vídeo e áudio ainda sem carregar, ocupando altura zero. Cada
  // um que termina de carregar empurra o conteúdo para baixo, e sem isto a
  // conversa fica parada onde estava — no meio.
  //
  // O ResizeObserver avisa a cada mudança de altura do conteúdo, venha ela de
  // mídia, de fonte ou do redimensionamento da janela. A trava `grudarNoFim`
  // é o que impede o efeito oposto: quem subiu para ler o histórico não pode
  // ser arrastado de volta a cada anexo que carrega.
  useEffect(() => {
    const lista = document.getElementById("messagesList");
    const conteudo = lista?.firstElementChild;

    if (!lista || !conteudo) return undefined;

    const observador = new ResizeObserver(() => {
      if (grudarNoFim.current) lista.scrollTop = lista.scrollHeight;
    });

    observador.observe(conteudo);

    return () => observador.disconnect();
  }, [ticketId]);

  // Trocar de atendimento sempre abre no fim, mesmo que a conversa anterior
  // tenha ficado parada no meio do histórico.
  useEffect(() => {
    grudarNoFim.current = true;
  }, [ticketId]);

  // Vai para o fim da conversa.
  //
  // Era `lastMessageRef.scrollIntoView({})`, e havia dois problemas. O
  // primeiro: sem `block`, o padrão é "start" — o navegador alinha a âncora ao
  // TOPO da área visível, não ao fim. O segundo, o que fazia a conversa abrir
  // no meio: a rolagem acontece assim que as mensagens entram na lista, mas
  // imagem, vídeo e áudio ainda não carregaram e ocupam altura zero. Quando
  // carregam, o conteúdo cresce POR BAIXO do ponto onde paramos, e a conversa
  // fica parada no meio. Numa conversa cheia de mídia — as do Instagram, por
  // exemplo — é sempre assim.
  //
  // Mexer no `scrollTop` do contêiner é exato e não depende de âncora nenhuma;
  // o `grudarNoFim` abaixo é o que segura a posição enquanto a mídia carrega.
  const scrollToBottom = () => {
    const lista = document.getElementById("messagesList");

    if (lista) lista.scrollTop = lista.scrollHeight;
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // Uma folga de 80px: quem está "quase" no fim continua sendo levado junto
    // pela mensagem nova, como em qualquer mensageiro.
    grudarNoFim.current = scrollHeight - scrollTop - clientHeight < 80;

    if (!hasMore) return;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  // Dois cliques na bolha respondem aquela mensagem.
  //
  // Responder citando já existia, mas escondido atrás da setinha que só aparece
  // ao passar o mouse: eram três passos (achar a seta, abrir o menu, escolher
  // "Responder") para o gesto mais comum de uma conversa. O duplo clique é o
  // mesmo atalho do WhatsApp Web.
  const responderMensagem = (message) => {
    if (modoSelecao || message.isDeleted || message.isInternal) return;

    // O duplo clique seleciona a palavra sob o cursor; sem limpar, fica um
    // trecho azulado no meio da conversa sem motivo nenhum.
    window.getSelection()?.removeAllRanges();

    setReplyingMessage(message);
  };

  // Ir até a mensagem citada — reaproveita o caminho da busca, que sabe puxar
  // páginas para trás quando a mensagem ainda não foi carregada.
  const irParaMensagem = (id) => {
    if (!id) return;

    paginasPuxadasRef.current = 0;
    setAlvoBusca(id);
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const alternarFavorito = async (message) => {
    const jaEra = favoritos.has(message.id);

    try {
      if (jaEra) {
        await api.delete(`/messages/${message.id}/favorite`);
      } else {
        await api.post(`/messages/${message.id}/favorite`);
      }

      setFavoritos((anterior) => {
        const novo = new Set(anterior);
        if (jaEra) novo.delete(message.id);
        else novo.add(message.id);
        return novo;
      });

      // Desfavoritar enquanto se olha só as favoritas: a mensagem sai da
      // lista na hora, senão fica ali contrariando o filtro.
      if (jaEra && apenasFavoritas) {
        dispatch({ type: "REMOVE_MESSAGE", payload: message.id });
      }
    } catch (err) {
      toastError(err);
    }
  };

  // Estrela de favoritar. Só aparece ao passar o mouse — a não ser quando já
  // está marcada, que é justamente o que se quer enxergar rolando a conversa.
  const renderFavoritoBotao = (message) => {
    const favorita = favoritos.has(message.id);

    return (
      <button
        type="button"
        title={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-label={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-pressed={favorita}
        onClick={() => alternarFavorito(message)}
        className={cn(
          "absolute right-7 top-0 h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground hover:bg-black/5",
          favorita ? "flex text-amber-500" : "hidden group-hover:flex",
        )}
      >
        <Star className={cn("h-4 w-4", favorita && "fill-amber-400")} />
      </button>
    );
  };

  // Chips de reação, colados na base do balão. O total só aparece a partir de
  // dois: com uma pessoa só o número não informa nada e polui a conversa.
  const renderReacoes = (message) => {
    if (!message.reacoes?.length) return null;

    return (
      <div className="-mb-1 flex flex-wrap gap-1 pt-1">
        {message.reacoes.map((reacao) => (
          <button
            key={reacao.emoji}
            type="button"
            onClick={() => reagir(message, reacao.emoji)}
            title={
              reacao.euReagi ? "Remover sua reação" : "Reagir com este emoji"
            }
            aria-pressed={reacao.euReagi}
            className={cn(
              "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs leading-none transition-colors",
              reacao.euReagi
                ? "border-emerald-400 bg-emerald-100 dark:border-emerald-500 dark:bg-emerald-800/60"
                : "border-transparent bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20",
            )}
          >
            <span>{reacao.emoji}</span>
            {reacao.total > 1 && (
              <span className="text-[10px] text-muted-foreground">
                {reacao.total}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  // Numa mensagem de mídia o corpo é a legenda. Mensagens antigas guardavam o
  // nome do arquivo ali — nesse caso não há legenda de verdade para mostrar.
  const corpoVisivel = (message) => {
    // Localização: o cartão do mapa já mostra nome, endereço e coordenadas.
    // Sem isto, o corpo cru ("|https://...|Nome do lugar") era impresso
    // embaixo do cartão, com o separador e tudo.
    if (message.mediaType === "location" && lerLocalizacao(message.body)) {
      return "";
    }

    // Contato: o corpo da mensagem é o vCard inteiro. Sem isto, o balão
    // desenhava o cartão e logo abaixo imprimia "BEGIN:VCARD VERSION:3.0
    // N:...;...;;; FN:... item1.TEL;waid=...:+55 ... END:VCARD" como se fosse
    // texto que alguém escreveu.
    if (message.mediaType === "vcard") {
      return "";
    }

    if (!message.mediaUrl || !message.body) return message.body;

    const arquivo = decodeURIComponent(message.mediaUrl.split("/").pop() || "");

    return message.body.trim() === arquivo ? "" : message.body;
  };

  // Anexos que estão à vista na conversa, do mais antigo para o mais novo — é
  // a ordem em que a conversa aconteceu, e é por ela que as setas do
  // visualizador andam.
  const arquivosDaConversa = messagesList
    .filter((m) => m.mediaUrl && !m.isDeleted)
    .map((m) => ({
      id: m.id,
      url: m.mediaUrl,
      mediaType: m.mediaType,
      legenda: corpoVisivel(m),
      fromMe: m.fromMe,
      createdAt: m.createdAt,
    }));

  const abrirArquivo = (messageId) => {
    const indice = arquivosDaConversa.findIndex((a) => a.id === messageId);
    if (indice >= 0) setVisualizando(indice);
  };

  const alternarSelecao = (messageId) => {
    setSelecionadas((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(messageId)) novo.delete(messageId);
      else novo.add(messageId);
      return novo;
    });
  };

  const todosArquivosMarcados =
    arquivosDaConversa.length > 0 &&
    arquivosDaConversa.every((a) => selecionadas.has(a.id));

  const alternarTodosDaConversa = () => {
    setSelecionadas(() =>
      todosArquivosMarcados
        ? new Set()
        : new Set(arquivosDaConversa.map((a) => a.id)),
    );
  };

  const sairDaSelecao = () => {
    setModoSelecao(false);
    setSelecionadas(new Set());
  };

  // `ids` vazio significa "tudo o que este atendimento tem" — o backend monta
  // o ZIP a partir do banco, e não do que já foi rolado na tela. Sem isso,
  // "Baixar tudo" numa conversa longa levaria só as mensagens carregadas.
  const baixarArquivos = async (ids, rotulo) => {
    setBaixando(rotulo);
    try {
      const { data, headers } = await api.post(
        `/messages/${ticketId}/gallery/download`,
        { messageIds: ids },
        { responseType: "blob" },
      );

      const nome =
        (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
        `arquivos-atendimento-${ticketId}.zip`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("O download do ZIP começou.");
      if (ids.length) sairDaSelecao();
    } catch (err) {
      // Com responseType blob a mensagem de erro também chega como blob.
      if (err?.response?.data instanceof Blob) {
        try {
          const texto = await err.response.data.text();
          err.response.data = JSON.parse(texto);
        } catch {
          // Fica o erro genérico mesmo.
        }
      }
      toastError(err);
    } finally {
      setBaixando(null);
    }
  };

  const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
  const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

  const IconeDoDocumento = ({ extensao }) => {
    if (PLANILHAS.includes(extensao))
      return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
    if (COMPACTADOS.includes(extensao))
      return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
    return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
  };

  // No modo seleção, uma capa transparente cobre a bolha inteira e recebe o
  // clique. Sem ela, clicar numa imagem abriria o lightbox e num vídeo daria
  // play — o clique nunca chegaria na seleção.
  const renderCapaDeSelecao = (message) => {
    if (!modoSelecao || !message.mediaUrl) return null;

    const marcada = selecionadas.has(message.id);

    return (
      <button
        type="button"
        onClick={() => alternarSelecao(message.id)}
        aria-pressed={marcada}
        aria-label={marcada ? "Desmarcar arquivo" : "Marcar arquivo"}
        className="absolute inset-0 z-10 rounded-2xl"
      >
        <span
          className={cn(
            "absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-background/90",
            marcada
              ? "border-primary bg-primary text-primary-foreground"
              : "border-muted-foreground/50",
          )}
        >
          {marcada && <Check className="h-3 w-3" />}
        </span>
      </button>
    );
  };

  // Classes que a bolha ganha enquanto se escolhe arquivo.
  const classesDeSelecao = (message) =>
    modoSelecao &&
    (message.mediaUrl
      ? cn(
          "cursor-pointer",
          selecionadas.has(message.id) && "ring-2 ring-primary ring-offset-1",
        )
      : // Mensagem sem anexo não vai no ZIP; apagada evita a dúvida.
        "opacity-50");

  const adicionarAosContatos = async (contato) => {
    setAdicionando(contato.id);

    try {
      await api.put(`/contacts/${contato.id}/agenda`);
      setAdicionados((anteriores) => new Set(anteriores).add(contato.id));
      toast.success(`${contato.name} agora está nos seus contatos.`);
    } catch (err) {
      toastError(err);
    }

    setAdicionando(null);
  };

  // Quem falou no grupo — com a marca de "já está nos meus contatos".
  //
  // Todo participante vira contato no banco para a mensagem ter dono, então
  // "existe em Contacts" não responde nada. O que a equipe precisa saber é se
  // aquela pessoa é conhecida da empresa ou um número qualquer que apareceu
  // no grupo; quando não é, o botão adiciona sem tirar ninguém da conversa.
  const renderAutorDoGrupo = (message) => {
    const contato = message.contact;

    if (!contato) return null;

    const naAgenda = contato.savedInAgenda || adicionados.has(contato.id);

    return (
      <span className="flex items-center gap-1 font-medium text-sky-500">
        {contato.name}
        {naAgenda ? (
          <BadgeCheck
            className="h-3.5 w-3.5 text-emerald-500"
            title="Já está nos seus contatos"
          />
        ) : (
          <button
            type="button"
            onClick={() => adicionarAosContatos(contato)}
            disabled={adicionando === contato.id}
            title="Adicionar aos contatos"
            aria-label={`Adicionar ${contato.name} aos contatos`}
            className="flex items-center gap-0.5 rounded-full border border-dashed px-1.5 text-[10px] font-normal text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <UserPlus className="h-3 w-3" />
            adicionar
          </button>
        )}
      </span>
    );
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location") {
      // A leitura do corpo mora em utils/localizacao: são três formatos de
      // gravação convivendo na mesma conversa (o atual, o antigo de duas
      // partes e o do Telegram, sem separador nenhum).
      const local = lerLocalizacao(message.body);

      if (local) {
        return (
          <LocationPreview
            image={local.imagem}
            link={local.link}
            description={local.descricao}
            latitude={local.latitude}
            longitude={local.longitude}
          />
        );
      }
    }

    if (message.mediaType === "vcard") {
      // Uma mensagem pode trazer vários contatos emendados — é o que o
      // WhatsApp manda quando se encaminha mais de um de uma vez.
      const cartoes = lerVcards(message.body);

      if (!cartoes.length) return null;

      return (
        <div className="-mx-3 -mb-1.5 -mt-2 divide-y divide-black/10 dark:divide-white/15">
          {cartoes.map((cartao, indice) => (
            <VcardPreview
              key={`${cartao.numeros[0] || cartao.nome}-${indice}`}
              contact={cartao.nome}
              numbers={cartao.numeros[0]}
            />
          ))}
        </div>
      );
    } else if (message.mediaType === "sticker") {
      // Figurinha não é foto: no WhatsApp ela aparece sem balão, com o fundo
      // transparente do desenho aparecendo, e num tamanho menor. Tratada como
      // imagem comum, virava um quadrado de 250x250 com fundo branco dentro
      // da bolha — a diferença entre "mandaram uma figurinha" e "mandaram um
      // print" se perdia.
      return (
        <img
          src={message.mediaUrl}
          alt="Figurinha"
          loading="lazy"
          decoding="async"
          className="h-[130px] w-[130px] object-contain"
        />
      );
    } else if (message.mediaType === "image") {
      // Antes havia também uma checagem de extensão (jpe?g|png|gif). Ela era
      // redundante — o backend já deriva mediaType do mimetype — e deixava de
      // fora tudo o mais que o WhatsApp manda: .webp (o formato padrão de
      // figurinha e de foto encaminhada), .heic do iPhone, .bmp, .avif. O que
      // não casava virava botão de download, e a conversa ficava com um
      // "Download" no lugar da imagem.
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return (
        <>
          <Audio url={message.mediaUrl} />
          <TranscricaoDeAudio message={message} />
        </>
      );
    } else if (message.mediaType === "video") {
      return <VideoNaConversa url={message.mediaUrl} />;
    } else {
      // Documento. Antes era só um botão "Download": para saber se o PDF era
      // o contrato certo, a pessoa baixava, abria fora e voltava. Agora o
      // cartão diz o que é o arquivo e abre no visualizador, sem sair daqui.
      const extensao = extensaoDe(message.mediaUrl);
      const rotulo = extensao
        ? `Arquivo ${extensao.replace(".", "").toUpperCase()}`
        : "Arquivo";

      return (
        <div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
          {/* PDF mostra a primeira página; os outros formatos não têm como
              ser desenhados pelo navegador e ficam só no cartão. */}
          {extensao === ".pdf" && (
            <div className="mb-2">
              <MiniaturaDePdf
                url={message.mediaUrl}
                onAbrir={() => abrirArquivo(message.id)}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
          <IconeDoDocumento extensao={extensao} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{rotulo}</p>
            <p className="text-xs text-muted-foreground">
              {extensao ? extensao.replace(".", "").toUpperCase() : "documento"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            title="Visualizar"
            onClick={() => abrirArquivo(message.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0">
            <a
              href={message.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Baixar"
            >
              <Download className="h-4 w-4" />
            </a>
          </Button>
          </div>
        </div>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <Clock className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 1) {
      return <Check className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 2) {
      return <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle text-emerald-500" />
      );
    }
  };

  // "Hoje" e "Ontem" poupam quem lê de converter a data mentalmente; só a
  // partir de anteontem o número passa a ser mais informativo que o rótulo.
  const rotuloDoDia = (data) => {
    const dia = parseISO(data);

    if (isSameDay(dia, new Date())) return "Hoje";
    if (isSameDay(dia, subDays(new Date(), 1))) return "Ontem";

    return format(dia, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <div
          key={`timestamp-${message.id}`}
          className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
        >
          {rotuloDoDia(messagesList[index].createdAt)}
        </div>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <div
            key={`timestamp-${message.id}`}
            className="sticky top-1 z-10 mx-auto my-3 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
          >
            {rotuloDoDia(messagesList[index].createdAt)}
          </div>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  // A faixa que separa um atendimento do outro.
  //
  // A conversa traz o histórico do contato, e sem esta marca as falas de três
  // chamados diferentes viravam um bloco só: ninguém distinguia o que foi dito
  // no problema de ontem do que está sendo tratado agora. Traz o número e a
  // fila, que é como a equipe se refere ao chamado.
  const renderFaixaDoAtendimento = (message, index) => {
    const anterior = messagesList[index - 1];

    // No modelo de 1 atendimento por contato o número do ticket não muda: o
    // cliente volta a escrever e a conversa continua no mesmo ticket. O que
    // separa um atendimento do outro é a faixa de encerramento, então depois
    // dela a faixa com o número aparece de novo — é onde o novo começa.
    if (
      anterior &&
      anterior.ticketId === message.ticketId &&
      anterior.mediaType !== "atendimentoEncerrado"
    ) {
      return null;
    }

    const dados = atendimentos[message.ticketId];
    const numero = message.ticketId;
    const fila = dados?.fila;
    const atual = Number(ticketId) === Number(numero);

    return (
      <div
        key={`atendimento-${numero}-${message.id}`}
        className="my-3 flex items-center gap-2 self-stretch"
      >
        <span className="h-px flex-1 bg-border" />
        <span
          className={cn(
            "w-fit rounded-full px-3 py-1 text-[11px] font-medium shadow-xs",
            atual
              ? "bg-primary text-primary-foreground"
              : "border bg-background/85 text-muted-foreground backdrop-blur-sm",
          )}
          title={
            dados?.status === "closed"
              ? "Atendimento encerrado"
              : "Atendimento em andamento"
          }
        >
          #Atendimento {numero}
          {fila ? ` — ${fila.toUpperCase()}` : ""}
          {dados?.status === "closed" ? " · encerrado" : ""}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return <span className="mt-4" key={`divider-${message.id}`}></span>;
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        role="button"
        tabIndex={0}
        // Clicar na citação leva à mensagem original — sem isso a citação diz
        // "sobre isto aqui" e deixa a pessoa rolando a conversa à mão.
        title="Ir para a mensagem citada"
        onClick={() => irParaMensagem(message.quotedMsg?.id)}
        onKeyDown={(e) =>
          e.key === "Enter" && irParaMensagem(message.quotedMsg?.id)
        }
        // O duplo clique aqui dentro não deve virar "responder a citação".
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(
          "-mx-1.5 -my-0.5 mb-1.5 flex cursor-pointer overflow-hidden rounded-lg text-left hover:brightness-95",
          message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "w-1 shrink-0",
            message.quotedMsg?.fromMe ? "bg-emerald-400" : "bg-sky-400",
          )}
        />
        <div className="min-w-0 overflow-hidden whitespace-pre-wrap p-2.5 text-sm">
          {!message.quotedMsg?.fromMe && (
            <span className="flex font-medium text-sky-500">
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  // O canto reto de um lado só é o que dá a "ponta" da bolha e diz de quem
  // é a fala sem precisar de cor forte. Sombra fica quase imperceptível: com
  // centenas de bolhas na tela, sombra pesada vira ruído.
  const bubbleBase =
    "relative mt-0.5 block h-auto max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-2xl px-3 pb-1.5 pt-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] sm:max-w-[600px]";

  const ehFigurinha = (message) =>
    message.mediaType === "sticker" && !!message.mediaUrl && !message.isDeleted;

  // Figurinha fica fora do balão: no WhatsApp ela flutua no fundo da conversa,
  // e é isso que a distingue de um print que alguém mandou.
  const classesDeFigurinha = (message) =>
    ehFigurinha(message)
      ? "bg-transparent px-0 pb-5 pt-0 shadow-none dark:bg-transparent"
      : "";

  // Sem balão atrás, a hora sumiria em cima do desenho: ganha a mesma pílula
  // escura que o WhatsApp usa.
  const classesDaHora = (message) =>
    ehFigurinha(message) ? "rounded bg-black/50 px-1 py-px text-white" : "";

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        // Nota da equipe: acompanha o lado das mensagens enviadas, mas ganha
        // cor e cabeçalho próprios para jamais parecer algo que chegou ao
        // cliente.
        // Marca de fim de atendimento: faixa com o numero do chamado e a
        // fila (ou conexao), gravada quando o ticket foi encerrado. Vem como
        // mensagem interna, mas se desenha como divisoria, nao como balao.
        if (message.mediaType === "atendimentoEncerrado") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <div className="my-3 flex items-center gap-2 self-stretch">
                <span className="h-px flex-1 bg-border" />
                <span className="w-fit rounded-full border bg-background/85 px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm">
                  {message.body}
                  {" · "}
                  {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            </React.Fragment>
          );
        }

        if (message.isInternal) {
          return (
            <React.Fragment key={message.id}>
              {renderFaixaDoAtendimento(message, index)}
              {renderDailyTimestamps(message, index)}
              <div
                id={`mensagem-${message.id}`}
                className="relative my-1 ml-5 mr-4 max-w-[85%] self-end rounded-2xl rounded-br-md border border-indigo-200 bg-indigo-50 px-3 pb-5 pt-2 text-slate-800 shadow-[0_1px_2px_rgba(0,0,0,0.06)] sm:max-w-[600px] dark:border-indigo-800 dark:bg-indigo-950/55 dark:text-slate-100"
              >
                <div className="mb-1 flex items-center gap-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-300">
                  <LockKeyhole className="h-3 w-3 shrink-0" />
                  <span>
                    Mensagem privada · {message.internalAuthor?.name || "Equipe"}
                  </span>
                </div>
                <div className="whitespace-pre-wrap wrap-break-word text-sm">
                  <MarkdownWrapper mentions={message.mentionNames}>
                    {message.body}
                  </MarkdownWrapper>
                </div>
                <span
                  className="absolute bottom-1 right-2 text-[11px] text-muted-foreground"
                  title={format(
                    parseISO(message.createdAt),
                    "dd/MM/yyyy 'às' HH:mm:ss",
                  )}
                >
                  {format(
                    parseISO(message.createdAt),
                    horaDetalhada ? "HH:mm:ss" : "HH:mm",
                  )}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderFaixaDoAtendimento(message, index)}
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                id={`mensagem-${message.id}`}
                onDoubleClick={() => responderMensagem(message)}
                title="Dois cliques para responder"
                className={cn(
                  bubbleBase,
                  "group mr-5 self-start rounded-bl-md bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-100",
                  classesDeFigurinha(message),
                  destacada === message.id &&
                    "ring-2 ring-amber-400 ring-offset-1",
                  classesDeSelecao(message),
                )}
              >
                {renderCapaDeSelecao(message)}
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {renderFavoritoBotao(message)}
                {isGroup && renderAutorDoGrupo(message)}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") && (
                  // Apagada: a mídia continua, esmaecida. Some para o cliente,
                  // não para o histórico de quem atende.
                  <div className={cn(message.isDeleted && "opacity-50 grayscale")}>
                    {checkMessageMedia(message)}
                  </div>
                )}
                <div className="wrap-break-word pb-1 pr-16 pt-0.5">
                  {/* Repasse. Vale nos dois sentidos: o que o cliente
                      encaminhou (para a equipe não responder como se ele
                      tivesse escrito) e o que a equipe encaminhou. */}
                  {message.isForwarded && (
                    <span className="mb-0.5 flex items-center gap-1 text-[11px] italic text-muted-foreground">
                      <Forward className="h-3 w-3" />
                      Encaminhada
                    </span>
                  )}

                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.templateData ? (
                    <TemplateMessage dados={message.templateData} />
                  ) : (
                    <MarkdownWrapper mentions={message.mentionNames}>
                      {corpoVisivel(message)}
                    </MarkdownWrapper>
                  )}
                  {/* Editada: a marca e o que dizia antes. Sem o original, a
                      edição viraria um jeito silencioso de apagar o registro
                      do que foi dito ao cliente. */}
                  {message.editedAt && (
                    <span
                      className="ml-1 inline-flex cursor-help items-center gap-0.5 align-middle text-[10px] text-muted-foreground"
                      title={
                        message.previousBody
                          ? `Antes: ${message.previousBody}`
                          : "Mensagem editada"
                      }
                    >
                      <Pencil className="h-2.5 w-2.5" />
                      editada
                    </span>
                  )}

                  {renderReacoes(message)}
                  <span
                    className={cn(
                      "absolute bottom-0.5 right-1.5 text-[11px] text-muted-foreground",
                      classesDaHora(message),
                    )}
                  >
                    <span
                      // A hora completa fica no title: com duas mensagens no
                      // mesmo minuto — uma rajada do cliente, o eco de um
                      // envio — "14:32" nas duas não diz qual veio antes.
                      title={format(
                        parseISO(message.createdAt),
                        "dd/MM/yyyy 'às' HH:mm:ss",
                      )}
                    >
                      {format(parseISO(message.createdAt), horaDetalhada ? "HH:mm:ss" : "HH:mm")}
                    </span>
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderFaixaDoAtendimento(message, index)}
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                // Âncora da busca: é por este id que o resultado escolhido
                // encontra o balão para rolar até ele.
                id={`mensagem-${message.id}`}
                onDoubleClick={() => responderMensagem(message)}
                title="Dois cliques para responder"
                className={cn(
                  bubbleBase,
                  "group ml-5 self-end rounded-br-md bg-[#dcf8c6] text-slate-800 dark:bg-emerald-900/45 dark:text-slate-100",
                  classesDeFigurinha(message),
                  destacada === message.id &&
                    "ring-2 ring-amber-400 ring-offset-1",
                  classesDeSelecao(message),
                )}
              >
                {renderCapaDeSelecao(message)}
                <button
                  type="button"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  className="absolute right-0 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {renderFavoritoBotao(message)}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") && (
                  // Apagada: a mídia continua, esmaecida. Some para o cliente,
                  // não para o histórico de quem atende.
                  <div className={cn(message.isDeleted && "opacity-50 grayscale")}>
                    {checkMessageMedia(message)}
                  </div>
                )}
                <div
                  className={cn(
                    "wrap-break-word pb-1 pr-16 pt-0.5",
                    message.isDeleted && "italic text-muted-foreground",
                  )}
                >
                  {/* Apagada não é uma faxina: a faixa diz que sumiu para o
                      cliente, e o conteúdo fica embaixo, esmaecido. "Mensagem
                      apagada" sozinho não conta nada a quem abre a conversa
                      depois e precisa entender do que se tratava. */}
                  {message.isDeleted && (
                    <span className="mb-1 flex items-center gap-1 text-[11px] not-italic">
                      <Ban className="h-3.5 w-3.5" />
                      Apagada para todos
                      {message.deletedAt
                        ? ` · ${format(parseISO(message.deletedAt), "HH:mm")}`
                        : ""}
                    </span>
                  )}
                  {/* Repasse. Vale nos dois sentidos: o que o cliente
                      encaminhou (para a equipe não responder como se ele
                      tivesse escrito) e o que a equipe encaminhou. */}
                  {message.isForwarded && (
                    <span className="mb-0.5 flex items-center gap-1 text-[11px] italic text-muted-foreground">
                      <Forward className="h-3 w-3" />
                      Encaminhada
                    </span>
                  )}

                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.templateData ? (
                    <TemplateMessage dados={message.templateData} />
                  ) : (
                    <MarkdownWrapper mentions={message.mentionNames}>
                      {corpoVisivel(message)}
                    </MarkdownWrapper>
                  )}
                  {renderReacoes(message)}
                  <span
                    className={cn(
                      "absolute bottom-0.5 right-1.5 flex items-center text-[11px] text-muted-foreground",
                      classesDaHora(message),
                    )}
                  >
                    <span
                      // A hora completa fica no title: com duas mensagens no
                      // mesmo minuto — uma rajada do cliente, o eco de um
                      // envio — "14:32" nas duas não diz qual veio antes.
                      title={format(
                        parseISO(message.createdAt),
                        "dd/MM/yyyy 'às' HH:mm:ss",
                      )}
                    >
                      {format(parseISO(message.createdAt), horaDetalhada ? "HH:mm:ss" : "HH:mm")}
                    </span>
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else if (filtrando && !loading) {
      // Sem isto, filtrar e não achar nada mostrava "Say hello to your new
      // contact!" — quem lia achava que a conversa tinha sumido.
      return (
        <div className="m-auto rounded-lg border border-dashed bg-background/70 px-4 py-3 text-center text-sm text-muted-foreground">
          {apenasFavoritas
            ? "Nenhuma mensagem favoritada neste recorte."
            : "Nenhuma mensagem no período escolhido."}
        </div>
      );
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
  };

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {/* Barra de filtros. Recolhida por padrão: na maior parte do tempo o que
          se quer é a conversa inteira, e três campos fixos aqui roubariam
          altura da leitura no celular. */}
      <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-background px-3 py-1.5">
        <Button
          variant={filtrosAbertos || filtrando ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setFiltrosAbertos((aberto) => !aberto)}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtrar
        </Button>

        <Button
          variant={modoSelecao ? "secondary" : "ghost"}
          size="sm"
          onClick={() =>
            modoSelecao ? sairDaSelecao() : setModoSelecao(true)
          }
          title="Escolher arquivos da conversa para baixar"
        >
          <CheckSquare className="h-4 w-4" />
          Selecionar
        </Button>

        <Button
          variant={apenasFavoritas ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setApenasFavoritas((ativo) => !ativo)}
          title="Mostrar só as mensagens que você favoritou"
        >
          <Star
            className={cn(
              "h-4 w-4",
              apenasFavoritas && "fill-amber-400 text-amber-500",
            )}
          />
          Favoritas
        </Button>

        {filtrando && (
          <Button variant="ghost" size="sm" onClick={limparFiltros}>
            <X className="h-4 w-4" />
            Limpar
          </Button>
        )}

        {/* Hora com segundos. Duas mensagens no mesmo minuto — uma rajada do
            cliente, uma resposta que cruzou com ela — mostravam a mesma hora,
            e não dava para saber qual veio antes. */}
        <Button
          variant="ghost"
          size="sm"
          aria-pressed={horaDetalhada}
          title={
            horaDetalhada
              ? "Mostrando hora com segundos"
              : "Mostrar a hora com segundos"
          }
          onClick={() => setHoraDetalhada((v) => !v)}
          className={cn(horaDetalhada && "text-primary")}
        >
          <Clock className="h-4 w-4" />
          Segundos
        </Button>

        {/* A busca fica na mesma barra, mas não recorta a conversa: ela só
            aponta para onde ir, e o contador diz quantas vezes o termo
            aparece — como no WhatsApp. */}
        <div className="ml-auto flex items-center gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              placeholder="Buscar na conversa"
              aria-label="Buscar na conversa"
              className="h-8 w-40 pl-7 text-xs sm:w-52"
            />
          </div>

          {/* O histórico do que já foi atendido com esta pessoa: a busca da
              conversa só acha o que está na tela de agora. */}
          <HistoricoAtendimentos ticketId={ticketId} />

          {buscando && (
            <span className="text-xs text-muted-foreground">…</span>
          )}

          {!buscando && termoBusca.trim().length >= 2 && (
            <>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {achados.length
                  ? `${indiceAchado + 1} de ${achados.length}`
                  : "nada"}
                {/* De qual chamado é o resultado: a busca cobre a conversa
                    inteira do contato, e sem isto não dá para saber se o que
                    foi encontrado é de hoje ou do atendimento do mês passado. */}
                {achados[indiceAchado] &&
                  Number(achados[indiceAchado].ticketId) !==
                    Number(ticketId) && (
                    <span className="ml-1 text-primary">
                      · #{achados[indiceAchado].ticketId}
                    </span>
                  )}
              </span>

              {achados.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Resultado anterior (mais recente)"
                    onClick={() =>
                      irParaAchado(
                        (indiceAchado - 1 + achados.length) % achados.length,
                      )
                    }
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Próximo resultado (mais antigo)"
                    onClick={() =>
                      irParaAchado((indiceAchado + 1) % achados.length)
                    }
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Limpar a busca"
                onClick={limparBusca}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {filtrosAbertos && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              De
              <Input
                type="date"
                value={dataInicio}
                max={dataFim || undefined}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Até
              <Input
                type="date"
                value={dataFim}
                min={dataInicio || undefined}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
          </div>
        )}
      </div>

      {/* Barra do modo seleção. Fica presa acima da conversa: rolando atrás
          dela, o que foi marcado e o botão de baixar continuam à mão. */}
      {modoSelecao && (
        <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-primary/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selecionadas.size} arquivo{selecionadas.size === 1 ? "" : "s"}{" "}
            selecionado{selecionadas.size === 1 ? "" : "s"}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={alternarTodosDaConversa}
            disabled={!arquivosDaConversa.length}
          >
            {todosArquivosMarcados ? "Desmarcar todos" : "Marcar todos à vista"}
          </Button>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() =>
                baixarArquivos(Array.from(selecionadas), "selecionados")
              }
              disabled={!selecionadas.size || !!baixando}
            >
              {baixando === "selecionados" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar selecionados ({selecionadas.size})
            </Button>

            {/* Lista vazia = tudo o que o atendimento tem no banco, e não só o
                que já foi rolado na tela. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => baixarArquivos([], "tudo")}
              disabled={!!baixando}
            >
              {baixando === "tudo" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar tudo da conversa
            </Button>

            <Button variant="ghost" size="sm" onClick={sairDaSelecao}>
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          </div>

          <p className="w-full text-xs text-muted-foreground">
            Toque nas mensagens com anexo para marcar. Imagens, vídeos, áudios,
            PDFs e documentos entram num ZIP separado por tipo.
          </p>
        </div>
      )}

      <VisualizadorDeArquivo
        open={visualizando !== null}
        onClose={() => setVisualizando(null)}
        arquivos={arquivosDaConversa}
        indice={visualizando ?? 0}
        onIndice={setVisualizando}
      />

      {/* A textura é clara; no tema escuro ela entra esmaecida sobre o fundo
          do tema para as bolhas não perderem contraste. */}
      <div
        id="messagesList"
        onScroll={handleScroll}
        className="scrollbar-discreta relative flex flex-1 flex-col overflow-y-auto bg-muted/40"
      >
        {/* Este wrapper existe para a textura ter em que se ancorar.
            Ela era `absolute inset-0` direto no contêiner de rolagem, e
            elemento absoluto ali é dimensionado pela área VISÍVEL, não pela
            altura do conteúdo: passando de uma tela de mensagens, o resto
            rolava para fora da textura e o fundo ficava branco.
            Aqui a altura acompanha as mensagens, e o min-h-full garante a
            textura inteira também quando há pouca conversa. O shrink-0 é
            obrigatório: o pai é flex-col, e item flex encolhe para caber em
            vez de crescer — sem ele o wrapper voltava a ter a altura da
            janela e o problema continuava. */}
        <div className="relative flex min-h-full shrink-0 flex-col p-3 pb-24 sm:p-5 sm:pb-5">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-repeat opacity-100 dark:opacity-[0.06]"
            style={{ backgroundImage: `url(${whatsBackground})` }}
          />
          <div className="relative flex flex-1 flex-col">
            {/* Antes o `renderMessages` só era chamado com a lista cheia, e
                por isso o aviso de "nada encontrado" nunca aparecia. */}
            {messagesList.length > 0 || filtrando ? renderMessages() : []}
          </div>
        </div>
      </div>
      {loading && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500 opacity-70" />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
